import { useAppSelector } from 'hooks/reduxHooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getLoyaltyEsimInclusion } from 'loyalty/constants/loyaltyEsimInclusion'
import type { LoyaltyEligibleOffer } from 'loyalty/lib/esimOfferEligibility'
import { getIsOfferEsimEligible, getIsTourVariationEsimEligible } from 'loyalty/lib/esimOfferEligibility'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'

function useLoyaltyEsimDescription(): string | undefined {
  const intl = useIntl()
  const benefit = useLoyaltyUserBenefitView('esim_package')
  const gbValue = benefit?.benefit?.value?.amount
  if (!gbValue) return undefined
  return intl.formatMessage({
    id: 'bhQ3Y-YBqnnZJOUmKfLe',
    defaultMessage: '{gbValue}GB Free Roaming Data',
    description: 'eSIM benefit inclusion line on search and offer tiles',
  }, { gbValue })
}

export default function useLoyaltyEsimInclusion(offer: LoyaltyEligibleOffer | undefined): App.OfferInclusion | undefined {
  const isEsimEligible = useAppSelector((state) => (offer ? getIsOfferEsimEligible(state, offer) : false))
  const description = useLoyaltyEsimDescription()

  return useMemo(() => {
    if (!offer || !isEsimEligible || !description) return undefined
    return getLoyaltyEsimInclusion(description)
  }, [offer, isEsimEligible, description])
}

/**
 * Tour-variation counterpart of useLoyaltyEsimInclusion.
 * Returns the synthetic eSIM OfferInclusion when the variation is eSIM-eligible, undefined otherwise.
 */
export function useTourVariationLoyaltyEsimInclusion(
  variation: App.Tours.TourV2OfferVariation | undefined,
): App.OfferInclusion | undefined {
  const isEsimEligible = useAppSelector((state) => (variation ? getIsTourVariationEsimEligible(state, variation) : false))
  const description = useLoyaltyEsimDescription()

  return useMemo(() => {
    if (!variation || !isEsimEligible || !description) return undefined
    return getLoyaltyEsimInclusion(description)
  }, [variation, isEsimEligible, description])
}
